import { ComponentType, forwardRef, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';

import { AnalyticsProps, buildAnalyticsAttributes } from 'support/helpers/analytics/analytics';
import { cn } from 'support/helpers/cn/cn';

import { MenuContext, MenuContextValue, MenuListContext, useMenuContext, useMenuListContext } from './contexts';
import { defaultContainerClasses, SIZE_CLASSNAMES } from './styles';

type MenuButtonNestedOptionProps = {
  label: ReactNode;
  Icon?: ComponentType<any>;
  children: ReactNode;
  testId?: string;
  className?: string;
  newGroup?: boolean;
  placement?: Placement;
} & AnalyticsProps;

export const MenuButtonNestedOption = forwardRef<HTMLButtonElement, MenuButtonNestedOptionProps>(
  function MenuButtonNestedOption(
    { label, Icon, children, testId, className, newGroup, placement = 'right-start', analyticsId, analyticsProperties },
    propRef,
  ) {
    const parentMenu = useMenuContext();
    const parentList = useMenuListContext();
    const tree = useFloatingTree();
    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const itemRef = useRef<HTMLButtonElement | null>(null);
    const itemIndexRef = useRef<number>(-1);
    const labelText = typeof label === 'string' ? label : undefined;

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [hasFocusInside, setHasFocusInside] = useState(false);

    const listItemsRef = useRef<Array<HTMLElement | null>>([]);
    const listContentRef = useRef<Array<string | null>>([]);

    const { refs, floatingStyles, context } = useFloating({
      nodeId,
      open: isOpen,
      onOpenChange: setIsOpen,
      placement,
      strategy: 'fixed',
      whileElementsMounted: autoUpdate,
      middleware: [offset({ mainAxis: 0, alignmentAxis: -4 }), flip(), shift({ padding: 8 })],
    });

    const hover = useHover(context, {
      enabled: parentId !== null,
      delay: { open: 75 },
      handleClose: safePolygon({ blockPointerEvents: true }),
    });
    const click = useClick(context, { event: 'mousedown', toggle: false, ignoreMouse: parentId !== null });
    const dismiss = useDismiss(context, { bubbles: { escapeKey: false, outsidePress: true } });
    const role = useRole(context, { role: 'menu' });
    const listNavigation = useListNavigation(context, {
      listRef: listItemsRef,
      activeIndex,
      nested: true,
      onNavigate: setActiveIndex,
    });
    const typeahead = useTypeahead(context, {
      listRef: listContentRef,
      activeIndex,
      onMatch: isOpen ? setActiveIndex : undefined,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
      hover,
      click,
      dismiss,
      role,
      listNavigation,
      typeahead,
    ]);

    useEffect(() => {
      if (!tree) {
        return;
      }
      const handleTreeClose = () => setIsOpen(false);
      tree.events.on('close', handleTreeClose);
      return () => {
        tree.events.off('close', handleTreeClose);
      };
    }, [tree]);

    const size = parentMenu.size ?? 'base';
    const containerClasses = cn(
      defaultContainerClasses,
      SIZE_CLASSNAMES[size],
      className,
      newGroup ? 'border-t border-gray-100' : undefined,
      isOpen ? 'bg-gray-200' : undefined,
    );

    const setRef = useCallback(
      (node: HTMLButtonElement | null) => {
        itemRef.current = node;
        refs.setReference(node);
        if (typeof propRef === 'function') {
          propRef(node);
        } else if (propRef) {
          propRef.current = node;
        }
        if (node) {
          const existingIndex = parentList.listItemsRef.current.indexOf(node);
          if (existingIndex === -1) {
            parentList.listItemsRef.current.push(node);
            itemIndexRef.current = parentList.listItemsRef.current.length - 1;
          } else {
            itemIndexRef.current = existingIndex;
          }
          parentList.listContentRef.current[itemIndexRef.current] = labelText ?? null;
        } else {
          parentList.listItemsRef.current = parentList.listItemsRef.current.filter((item) => item !== null);
        }
      },
      [refs, propRef, parentList.listItemsRef, parentList.listContentRef, labelText],
    );

    const innerMenuContextValue = useMemo<MenuContextValue>(
      () => ({
        getItemProps,
        activeIndex,
        setActiveIndex,
        setHasFocusInside,
        isOpen,
        size: parentMenu.size,
      }),
      [getItemProps, activeIndex, isOpen, parentMenu.size],
    );

    const referenceProps = parentMenu.getItemProps({
      ...getReferenceProps({
        onFocus: () => parentList.setHasFocusInside(true),
      }),
      role: 'menuitem',
      tabIndex: parentMenu.activeIndex === itemIndexRef.current ? 0 : -1,
    });

    return (
      <FloatingNode id={nodeId}>
        <button
          type="button"
          ref={setRef}
          data-testid={testId}
          className={containerClasses}
          {...buildAnalyticsAttributes({ analyticsId, analyticsProperties })}
          {...referenceProps}
        >
          {Icon && <Icon className="mr-2 -ml-1 size-5" />}
          <span className="flex-1">{label}</span>
          <ChevronRightIcon className="-mr-1 ml-2 size-5" aria-hidden="true" />
        </button>
        <MenuListContext.Provider value={{ listItemsRef, listContentRef, hasFocusInside, setHasFocusInside }}>
          <MenuContext.Provider value={innerMenuContextValue}>
            {isOpen && (
              <FloatingPortal>
                <FloatingFocusManager context={context} modal={false} initialFocus={-1} returnFocus={false}>
                  <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="z-60 w-fit overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus-visible:outline-hidden"
                    {...getFloatingProps()}
                  >
                    {children}
                  </div>
                </FloatingFocusManager>
              </FloatingPortal>
            )}
          </MenuContext.Provider>
        </MenuListContext.Provider>
      </FloatingNode>
    );
  },
);
